import React, { useContext, useEffect, useState, useCallback, useMemo, memo } from "react";
import { Container, Row } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { format, addDays } from "date-fns";
import { toast } from "react-toastify";
import { CiCalendar, CiClock2, CiLocationOn } from "react-icons/ci";
import { fetchEvents } from "../services/eventsApis";
import { FaPlus } from 'react-icons/fa';
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import enLocale from "@fullcalendar/core/locales/en-gb";
import NavBar from "../components/NavBar";
import FeedAdvertisement from "../components/feeds/sidebar/FeedAdvertisement";
import SuggestedCoursesEvents from "../components/feeds/sidebar/SuggestedCoursesEvents";
import CreateEventCard from "../components/events/create/CreateEventCard";
import EventTabs from "../components/events/EventTabs";
import UpcomingEventsCard from "../components/events/UpcomingEventsCard";
import MyEvents from "../components/events/MyEvents";
import JoinedEvents from "../components/events/JoinedEvents";
import SavedEvents from "../components/events/SavedEvents";
import { COURSES, EVENT_DETAILS, EVENTS } from "../constants/RoutesUrl";
import { FeedDataRefetch, Language } from "../App";
import CreateEventModal from "../components/events/create/CreateEventModal";
import { useCheckSubscriptionPermission } from "../hooks/useCheckSubscriptionPermission";
import { useMixpanelTracking } from "../hooks/useMixpanelTracking";
import { MixpanelEvents } from "../constants/MixpanelEvents";
import { useSplashAds } from "../hooks/useSplashAds";

const colors = ["#E1F9FF", "#FFEAD5", "#F5F5F5", "#FFF7DA", "#FEECFF", "#FFF3EE"];

const getColorForEvent = (title = "") => {
  const hash = title.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const splitMultiDayEvents = (events) => {
  return (events || []).flatMap((event) => {
    if (!event?.start || !event?.end || !event?.title) return [];
    const start = new Date(event.start);
    const end = new Date(event.end);
    if (isNaN(start) || isNaN(end)) return [];
    const color = getColorForEvent(event.title);
    const splitEvents = [];
    for (let d = start; d <= end; d = addDays(d, 1)) {
      splitEvents.push({
        title: event.title,
        start: format(d, "yyyy-MM-dd"),
        backgroundColor: color,
        textColor: "#000",
        borderColor: color,
        classNames: "cursor fs-8 roboto-bold",
      });
    }
    return splitEvents;
  });
};

const Events = () => {
  const { refetch } = useContext(FeedDataRefetch);
  const { lang } = useContext(Language);
  const { track } = useMixpanelTracking();
  const { ads: splashAds, isReady: areSplashAdsReady } = useSplashAds();
  const navigate = useNavigate();
  const location = useLocation();

  const [eventsData, setEventsData] = useState({
    all: [],
    thisMonth: [],
    upcoming: [],
    suggested: [],
    joined: [],
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventPosition, setEventPosition] = useState({ top: 0, left: 0 });
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "all");
  const [locale, setLocale] = useState("en");
  const [isOpen, setIsOpen] = useState(false);
  const [adsLoading, setAdsLoading] = useState(true);
  const checkPermission = useCheckSubscriptionPermission('upload_event');
  const toggle = () => {
    if (!checkPermission()) return;
    // Track create event event
    track(MixpanelEvents.createEvent, {
      timestamp: new Date().toISOString(),
    });
    setIsOpen(!isOpen);
  };

  const storedValue = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("contest_obj")) || {};
    } catch {
      return {};
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setAdsLoading(true)
    try {
      const data = await fetchEvents();
      if (!data) throw new Error("No data returned from API");
      setEventsData({
        all: data.all || [],
        thisMonth: data.this_month || [],
        upcoming: data.upcoming || [],
        suggested: data.suggested || [],
        joined: data.joined || [],
      });
      // Track app events event
      track(MixpanelEvents.appEvents, {
        total_events: data.all?.length || 0,
        timestamp: new Date().toISOString(),
      });
      setAdsLoading(false)
    } catch (error) {
      toast.error("Failed to load events. Please try again.", { autoClose: 3000 });
    }
  }, [track]);

  useEffect(() => {
    loadEvents();
  }, [refetch, loadEvents]);

  useEffect(() => {
    const browserLanguage = navigator.language || navigator.languages?.[0] || "en";
    setLocale(browserLanguage.startsWith("es") ? "es" : "en");
  }, []);

  const handleWheel = useCallback(
    debounce((e) => {
      const targetSection = e.target.closest("[data-scroll-section]");
      if (targetSection) {
        e.preventDefault();
        targetSection.scrollTop += e.deltaY;
      }
    }, 16, { leading: true, trailing: false }),
    []
  );

  const preventBodyScroll = useCallback((e) => {
    document.body.style.overflow = e.target.closest("[data-scroll-section]")
      ? "hidden"
      : "auto";
  }, []);

  useEffect(() => {
    document.addEventListener("wheel", handleWheel, { passive: false });
    document.addEventListener("mouseover", preventBodyScroll);
    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("mouseover", preventBodyScroll);
      document.body.style.overflow = "auto";
    };
  }, [handleWheel, preventBodyScroll]);

  const processedEvents = useMemo(() => splitMultiDayEvents(eventsData.all), [eventsData.all]);

  const adjustPopupPosition = useCallback((top, left) => {
    const popupWidth = 252;
    const popupHeight = 150; // Approximate height
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    return {
      top: Math.min(Math.max(top, popupHeight), windowHeight - 20),
      left: Math.min(Math.max(left, popupWidth / 2), windowWidth - popupWidth / 2),
    };
  }, []);

  const handleEventClick = useCallback((clickInfo) => {
    const eventFromApi = eventsData.all.find(
      (event) => event.title === clickInfo.event.title
    );
    if (eventFromApi) {
      const adjustedPosition = adjustPopupPosition(clickInfo.jsEvent.pageY, clickInfo.jsEvent.pageX);
      setSelectedEvent(eventFromApi);
      setEventPosition(adjustedPosition);
    }
  }, [eventsData.all, adjustPopupPosition]);

  const viewAllPurchased = useCallback((course_type, pageType, type) => {
    if (pageType === location.pathname) {
      // Track event view type event
      track(MixpanelEvents.eventViewType, {
        view_type: course_type,
        timestamp: new Date().toISOString(),
      });
      if (type === "courses") {
        navigate(COURSES, { state: { selectedCourseType: course_type } });
      } else if (type === "events") {
        navigate(`${EVENTS}/3`);
      }
      setActiveTab(course_type);
      window.scrollTo(0, 0);
    }
  }, [navigate, location.pathname, track]);

  const nextYearEndDate = useMemo(() => {
    const nextYear = new Date().getFullYear() + 1;
    return new Date(nextYear, 11, 31).toISOString().split("T")[0];
  }, []);

  const calendarConfig = useMemo(() => ({
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: "dayGridMonth",
    height: "100%",
    headerToolbar: { start: "title", center: "", end: "prev,next" },
    events: processedEvents,
    displayEventTime: false,
    displayEventEnd: false,
    eventClick: handleEventClick,
    validRange: {
      start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      end: nextYearEndDate,
    },
    showNonCurrentDates: false,
    fixedWeekCount: false,
    locale,
    locales: [enLocale, esLocale],
  }), [processedEvents, handleEventClick, locale, nextYearEndDate]);

  return (
    <Container fluid>
      <style>
        {`
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
      .hide-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      
      /* Mobile and Tablet responsive styles */
      @media (max-width: 1199.98px) {
        .app-mobile-tablet-calendar-wrapper {
          width: 100% !important;
          height: 500px !important;
        }
        .app-mobile-tablet-event-popup {
          width: 90vw !important;
          max-width: 300px !important;
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          z-index: 1050 !important;
        }
        .app-mobile-tablet-event-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1040;
        }
           .mobile-header {
          background-color: #ffffff;
          color: #1a202c;
          padding: 15px 15px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
          border-radius: 10px;
        }
        .mobile-header-text {
          font-size: 1.2rem;
          font-weight: bold;
          margin: 0;
        }
        .mobile-header-buttons {
          display: flex;
          gap: 10px;
        }
        .mobile-header-btn {
          background-color: #ffffff;
          border: 2px solid #1dc1dd;
          border-radius: 5px;
          padding: 5px 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .mobile-header-btn:hover {
          background-color: #f9fafb;
        }
        .mobile-header-btn svg {
          color: #1dc1dd;
          font-size: 1.2rem;
        }
      }
      
      @media (min-width: 768px) and (max-width: 1199.98px) {
        .app-mobile-tablet-calendar-wrapper {
          height: 600px !important;
        }
        .app-mobile-tablet-event-popup {
          max-width: 350px !important;
        }
      }
    `}
      </style>

      <Row>
        <NavBar />
      </Row>

      {/* Desktop Layout (1200px and above) */}
      <div
        className="d-none d-xl-flex justify-content-center"
        style={{
          padding: "5rem 1rem 3rem",
          maxWidth: "1360px",
          margin: "0 auto",
          width: "100%",
          gap: "24px",
        }}
      >
        {/* Left Sidebar */}
        <div
          data-scroll-section="left"
          style={{
            width: "335px",
            overflowY: "auto",
            maxHeight: "calc(100vh - 5rem)",
            minWidth: 0,
            flexShrink: 0,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
          className="hide-scrollbar"
        >
          <FeedAdvertisement ads={splashAds?.events || []} loading={!areSplashAdsReady} new_contest_obj={storedValue} />
          <CreateEventCard />
          <SuggestedCoursesEvents
            tabType="events"
            suggestedEvents={eventsData.suggested}
            joinedEvents={eventsData.joined}
            selectedCourseType={activeTab}
            isLoading={adsLoading}
            viewAllPurchased={() => viewAllPurchased("joined-events", "/events/3", "events")}
          />
        </div>

        {/* Main Content */}
        <div
          style={{
            flex: 1,
            maxWidth: "100%",
            minWidth: 0,
            overflowY: "auto",
            maxHeight: "calc(100vh - 5rem)",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
          data-scroll-section="center"
          className="hide-scrollbar"
        >
          <EventTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setSelectedEvent={setSelectedEvent}
          />
          {activeTab === "all" ? (
            <div className="my-3 bg-white rounded py-3" style={{ height: "853px" }}>
              <FullCalendar {...calendarConfig} />
            </div>
          ) : (
            <div className="my-3">
              {activeTab === "my-events" ? (
                <>
                  {track(MixpanelEvents.myEvents, {
                    timestamp: new Date().toISOString(),
                  })}
                  <MyEvents />
                </>
              ) : activeTab === "joined-events" ? (
                <>
                  {track(MixpanelEvents.joinedEvents, {
                    timestamp: new Date().toISOString(),
                  })}
                  <JoinedEvents />
                </>
              ) : (
                <>
                  {track(MixpanelEvents.savedEvents, {
                    timestamp: new Date().toISOString(),
                  })}
                  <SavedEvents />
                </>
              )}
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div
          className="position-relative hide-scrollbar"
          data-scroll-section="right"
          style={{
            overflowY: "auto",
            maxHeight: "calc(100vh - 5rem)",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            flexShrink: 0,
          }}
        >
          <UpcomingEventsCard thisMonth={eventsData.thisMonth} upcomingMonth={eventsData.upcoming} />
        </div>
      </div>

      {/* Mobile and Tablet Layout (below 1200px) */}
      <div className="d-xl-none" style={{ paddingTop: "5rem" }}>
        <div className="px-3">
          {/* Mobile/Tablet Header */}
          <div className="mobile-header">
            <h2 className="mobile-header-text">Explore Events Today!</h2>
            <div className="mobile-header-buttons">
              <div className="mobile-header-btn" onClick={toggle}>
                <FaPlus />
              </div>
            </div>
          </div>
          {isOpen && <CreateEventModal isOpen={isOpen} toggle={toggle} />}

          {/* Mobile/Tablet Event Tabs */}
          <EventTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setSelectedEvent={setSelectedEvent}
          />

          {/* Main Content Based on Active Tab */}
          {activeTab === "all" ? (
            <div className="my-3 bg-white rounded py-3 app-mobile-tablet-calendar-wrapper">
              <FullCalendar {...calendarConfig} />
            </div>
          ) : (
            <div className="my-3">
              {activeTab === "my-events" ? (
                <>
                  {track(MixpanelEvents.myEvents, {
                    timestamp: new Date().toISOString(),
                  })}
                  <MyEvents />
                </>
              ) : activeTab === "joined-events" ? (
                <>
                  {track(MixpanelEvents.joinedEvents, {
                    timestamp: new Date().toISOString(),
                  })}
                  <JoinedEvents />
                </>
              ) : (
                <>
                  {track(MixpanelEvents.savedEvents, {
                    timestamp: new Date().toISOString(),
                  })}
                  <SavedEvents />
                </>
              )}
            </div>
          )}

          {/* Mobile/Tablet Sections - Stacked Vertically */}
          <div className="mb-4">
            <h6 className="roboto-bold mb-3 text-secondary">Upcoming Events</h6>
            <div className="app-mobile-upcoming-events">
              <UpcomingEventsCard
                thisMonth={eventsData.thisMonth}
                upcomingMonth={eventsData.upcoming}
              />
            </div>
          </div>

          <div className="mb-4">
            <SuggestedCoursesEvents
              tabType="events"
              suggestedEvents={eventsData.suggested}
              joinedEvents={eventsData.joined}
              selectedCourseType={activeTab}
              viewAllPurchased={() => viewAllPurchased("joined-events", "/events/3", "events")}
            />
          </div>

          <div className="mb-4">
            <FeedAdvertisement ads={splashAds?.events || []} loading={!areSplashAdsReady} new_contest_obj={storedValue} />
          </div>
        </div>
      </div>

      {/* Event Popup - Works for desktop, tablet, and mobile */}
      {selectedEvent && (
        <>
          {/* Mobile/Tablet backdrop */}
          <div
            className="d-xl-none app-mobile-tablet-event-backdrop"
            onClick={() => setSelectedEvent(null)}
          />

          {/* Event popup */}
          <div
            className={`bg-white rounded p-3 ${window.innerWidth <= 1199 ? 'app-mobile-tablet-event-popup' : ''}`}
            style={
              window.innerWidth <= 1199
                ? {}
                : {
                  width: "252px",
                  position: "absolute",
                  top: `${eventPosition.top}px`,
                  left: `${eventPosition.left}px`,
                  transform: "translate(-50%, -100%)",
                  zIndex: 1,
                  boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
                }
            }
          >
            {/* Event Title */}
            {selectedEvent.title && (
              <div className="d-flex gap-2 align-items-center mb-2">
                <div
                  className="rounded d-flex justify-content-center align-items-center"
                  style={{ backgroundColor: "#EBEDF0", width: "20px", height: "20px" }}
                >
                  <CiCalendar size={12} />
                </div>
                <div>
                  <h5 className="roboto-bold mb-0 fs-10">{selectedEvent.title}</h5>
                </div>
              </div>
            )}

            {/* Event Time */}
            {selectedEvent.start && selectedEvent.end && (
              <div className="d-flex gap-2 align-items-center mb-2">
                <div
                  className="rounded d-flex justify-content-center align-items-center"
                  style={{ backgroundColor: "#EBEDF0", width: "20px", height: "20px" }}
                >
                  <CiClock2 size={12} />
                </div>
                <div>
                  <h5 className="roboto-regular mb-0 fs-10">
                    {selectedEvent.start} - {selectedEvent.end}
                  </h5>
                </div>
              </div>
            )}

            {/* Event Location */}
            {selectedEvent.short_location && (
              <div className="d-flex gap-2 align-items-center mb-2">
                <div
                  className="rounded d-flex justify-content-center align-items-center"
                  style={{ backgroundColor: "#EBEDF0", width: "20px", height: "20px" }}
                >
                  <CiLocationOn size={12} />
                </div>
                <div>
                  <h5 className="roboto-regular mb-0 fs-10">{selectedEvent.short_location}</h5>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <button
                className="bg-white rounded text-secondary px-3 py-1 roboto-regular fs-12 border border-inherit"
                onClick={() => setSelectedEvent(null)}
              >
                {lang?.cancel || "Cancel"}
              </button>
              <button
                onClick={() => navigate(`${EVENT_DETAILS}${selectedEvent.id || ''}`)}
                className="bg-white rounded px-3 py-1 roboto-bold fs-12"
                style={{ border: "1px solid #1DC1DD" }}
              >
                {lang?.view_details || "View Details"}
              </button>
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default memo(Events);
